/* ==========================================================================
   rt-gallery.js  —  Rawtorque product gallery behaviour
   Scope: #main-product only. Pairs with rt-gallery.css.

   Does five things:
     1. Strips pad_color/height from gallery image URLs so transparent PNGs
        stop rendering as white squares on the tile.
     2. Adds a zoom hint and a cursor-following zoom lens.
     3. Moves the red marker on the thumbnail rail.
     4. Preloads the full image on thumbnail hover intent.
     5. Tags <html> while a PDP image is in the lightbox, so the lightbox
        styling in rt-gallery.css applies to this gallery and nothing else.

   Never calls into Swiper or Fancybox internals. If either changes, the
   worst case is that the extras stop appearing; the gallery still works.
   ========================================================================== */
(function () {
  'use strict';

  var GAL_SEL = '#main-product .l4pr';
  /* Zoom gate. The catalogue clusters at 1000-1199px: at a 1200 gate only 29%
     of products could zoom, at 1000 it is 96.7%. Rather than one fixed
     magnification, the lens scales to what each file can actually support, so
     it never enlarges a master past roughly its own resolution. */
  var MIN_ZOOM_WIDTH  = 1000;
  var LENS_MIN_SCALE  = 1.35;  /* below this the zoom is not worth offering */
  var LENS_MAX_SCALE  = 2.40;  /* past this it stops reading as one product */
  var TILE_MIN_RATIO = 0.62;   /* widest tile allowed, height / width */
  var TILE_MAX_RATIO = 1.15;   /* tallest tile allowed */

  /* Hard ceiling on tile height. Supplier files are often square canvases with
     a landscape product floating in the middle, and matching that canvas gave
     an 824px tile that filled the whole viewport. The cap is whichever is
     smaller: a fixed pixel ceiling, or a share of the window height so short
     laptop screens get a shorter gallery. */
  var TILE_MAX_H_PX = 600;
  var TILE_MAX_H_VH = 0.62;

  var fine = window.matchMedia('(hover:hover) and (pointer:fine)');
  var calm = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------------- utils */

  function absolute(url) {
    if (!url) return '';
    url = url.trim();
    return url.indexOf('//') === 0 ? window.location.protocol + url : url;
  }

  /* Shopify bakes a white background in when pad_color is present and the
     requested box does not match the source aspect. Drop pad_color, and drop
     height/crop only when a width remains to size the image. */
  function depad(url) {
    if (!url) return url;
    var q = url.indexOf('?');
    if (q < 0) return url;

    var base = url.slice(0, q);
    var parts = url.slice(q + 1).split('&');
    var hasWidth = false;
    var i;

    for (i = 0; i < parts.length; i++) {
      if (parts[i].indexOf('width=') === 0) { hasWidth = true; break; }
    }

    var keep = [];
    for (i = 0; i < parts.length; i++) {
      var key = parts[i].split('=')[0];
      if (key === 'pad_color') continue;
      if (hasWidth && (key === 'height' || key === 'crop')) continue;
      keep.push(parts[i]);
    }
    return keep.length ? base + '?' + keep.join('&') : base;
  }

  function depadSrcset(value) {
    if (!value) return value;
    return value.split(',').map(function (entry) {
      var bits = entry.trim().split(/\s+/);
      if (!bits[0]) return entry;
      bits[0] = depad(bits[0]);
      return bits.join(' ');
    }).join(', ');
  }

  function depadImage(img) {
    if (!img || img.getAttribute('data-rt-depad') === '1') return;
    img.setAttribute('data-rt-depad', '1');

    var pic = img.parentNode;
    if (pic && pic.tagName === 'PICTURE') {
      var sources = pic.querySelectorAll('source');
      for (var s = 0; s < sources.length; s++) {
        if (sources[s].getAttribute('srcset')) {
          sources[s].setAttribute('srcset', depadSrcset(sources[s].getAttribute('srcset')));
        }
      }
    }
    if (img.getAttribute('srcset')) {
      img.setAttribute('srcset', depadSrcset(img.getAttribute('srcset')));
    }
    if (img.getAttribute('src')) {
      img.setAttribute('src', depad(img.getAttribute('src')));
    }
  }

  function depadAll(gal) {
    var imgs = gal.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) depadImage(imgs[i]);
  }

  /* ------------------------------------------------------------------ main */

  function setup(gal) {
    if (gal.getAttribute('data-rt-gallery') === '1') return;
    gal.setAttribute('data-rt-gallery', '1');

    depadAll(gal);

    var outer   = gal.querySelector('.swiper-outer');
    var bullets = gal.querySelector('.swiper-pagination-bullets');
    if (!outer) return;

    /* ---- injected furniture ---- */
    var lens = document.createElement('div');
    lens.className = 'rt-gal-lens';
    lens.setAttribute('aria-hidden', 'true');

    /* One affordance, not two. The `01 / 03` chip that used to sit here was
       redundant: the thumbnail rail is on screen at every breakpoint (a
       horizontal strip on phones, a vertical one on desktop) and already shows
       which image you are on, with a red ring and a rail marker. A filled chip
       repeating that number read as a sticker on the tile rather than part of
       it. Tapping or clicking the photo opens the viewer on every device, so
       nothing was lost by removing it. */
    var hint = document.createElement('span');
    hint.className = 'rt-gal-hint';
    hint.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" ' +
      'stroke-linecap="round"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>' +
      '<span class="rt-gal-hint-label">Zoom</span>';

    outer.appendChild(lens);
    outer.appendChild(hint);

    if (fine.matches) {
      /* decoration only: it fades in on hover and the click goes to the photo */
      hint.setAttribute('aria-hidden', 'true');
    } else {
      /* On touch there is no hover to reveal it, so it stays visible as the
         discoverable way in. Icon only, and a real button. */
      hint.setAttribute('data-tap', '1');
      hint.setAttribute('role', 'button');
      hint.setAttribute('tabindex', '0');
      hint.setAttribute('aria-label', 'Open image viewer');
      hint.addEventListener('click', openActive);
      hint.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openActive(); }
      });
    }

    function openActive() {
      var a = gal.querySelector('.swiper-slide-active a[data-fancybox]') ||
              gal.querySelector('a[data-fancybox]');
      if (a) a.click();
    }

    /* ---- state ---- */
    var lastIndex = -1;
    var zoomUrl = null;          /* full-size URL for the active slide, if big enough */
    var rect = null;             /* cached on pointerenter, never read during move */
    var probed = {};             /* url -> natural width */
    var lensScale = '100%';      /* computed on slide change, applied on hover */
    var lensLoaded = false;      /* has the big file been handed to the lens yet */
    var tileW = 0;               /* cached tile width; invalidated on resize */

    function tileWidth() {
      if (!tileW) tileW = outer.getBoundingClientRect().width || 680;
      return tileW;
    }

    function activeIndex() {
      if (!bullets) return 0;
      var all = bullets.querySelectorAll('.swiper-pagination-bullet');
      for (var i = 0; i < all.length; i++) {
        if (all[i].classList.contains('swiper-pagination-bullet-active')) return i;
      }
      return 0;
    }

    /* The theme fixes every slide at --ratio:1, which was sized for the old
       white-padded square. With real transparent art that leaves a large
       empty band above and below. Size the tile to the tallest image in the
       gallery instead — one shared ratio, so every slide matches. */
    var appliedRatio = null;

    function fitTiles() {
      /* Never resize the tile while the lightbox covers the page. Fancybox
         locks scrolling with padding-right on the body, which narrows this
         slider by the scrollbar width and looks exactly like a resize;
         recomputing then ran swiper.update() mid-animation and left the
         slides sized for one width inside a container of another, so the
         next slide showed through after closing. Guarding here rather than
         at each caller covers all four paths in: image load, slide sync,
         the ResizeObserver, and first run. The container is checked as well
         as our own class, because there is a short window after Fancybox
         appends the viewer but before ownership is confirmed. relayout()
         catches up on close. */
      if (document.documentElement.classList.contains('rt-pdp-lightbox') ||
          document.querySelector('.fancybox__container')) return;

      var imgs = gal.querySelectorAll('li img');   /* thumbs are not in <li> */
      var ratio = 0, i;

      for (i = 0; i < imgs.length; i++) {
        var im = imgs[i];

        /* Listen on every image, and not with { once }. An image keeps
           reporting its OLD natural size until a replacement src finishes
           loading, so de-padding leaves stale square dimensions behind for a
           moment. Re-running on each load is what makes the tile settle on
           the real aspect. appliedRatio keeps repeat runs cheap. */
        if (!im.getAttribute('data-rt-fit')) {
          im.setAttribute('data-rt-fit', '1');
          im.addEventListener('load', fitTiles);
        }
        if (im.naturalWidth) {
          ratio = Math.max(ratio, im.naturalHeight / im.naturalWidth);
        }
      }
      if (!ratio) return;

      ratio = Math.max(TILE_MIN_RATIO, Math.min(TILE_MAX_RATIO, ratio));

      /* Apply the height ceiling last, so it can take the tile below
         TILE_MIN_RATIO if the window is short. A gallery taller than the
         viewport is worse than a wide one. */
      var tileW = outer.getBoundingClientRect().width;
      if (tileW) {
        var capPx = Math.min(TILE_MAX_H_PX, window.innerHeight * TILE_MAX_H_VH);
        ratio = Math.min(ratio, capPx / tileW);
      }

      var value = ratio.toFixed(4);
      if (value === appliedRatio) return;
      appliedRatio = value;

      var lis = gal.querySelectorAll('li');
      for (i = 0; i < lis.length; i++) lis[i].style.setProperty('--ratio', value);

      /* Swiper is in autoHeight mode and caches the wrapper height, so it has
         to be told. Guarded: if the instance is gone the tiles are still right,
         there is just leftover space below until the next resize. */
      if (outer.swiper) {
        try { outer.swiper.update(); outer.swiper.updateAutoHeight(0); } catch (e) {}
      }
    }

    var markerH = 0;

    function moveMarker() {
      if (!bullets) return;
      var active = bullets.querySelector('.swiper-pagination-bullet-active');
      if (!active) return;

      /* Both reads happen before either write. Interleaving them makes the
         second read flush the first write's invalidation. */
      var y = active.offsetTop;
      var h = active.offsetHeight;

      bullets.style.setProperty('--rt-marker-y', y + 'px');
      /* height only changes on resize, and writing it re-invalidates the rail */
      if (h !== markerH) {
        markerH = h;
        bullets.style.setProperty('--rt-marker-h', h + 'px');
      }
      bullets.style.setProperty('--rt-marker-o', '1');
    }


    /* Only offer the lens where the master file is actually big enough.
       Your catalogue has 600px masters in places; a lens on those is mush. */
    /* Largest candidate in the slide's srcset. Shopify only generates
       candidates up to the original file, so the biggest w descriptor is the
       master width. Reading it is free.

       This used to be answered by downloading the full-size image with a
       throwaway Image() on every slide change, then handing that file straight
       to the lens as a background. Switching photo therefore cost a
       full-resolution fetch and decode each time - nine of them on a nine
       image gallery - which is what made paging through the gallery stutter. */
    function masterWidth(img) {
      var set = img ? img.getAttribute('srcset') : null;
      if (!set) return img && img.naturalWidth ? img.naturalWidth : 0;

      var best = 0;
      var parts = set.split(',');
      for (var i = 0; i < parts.length; i++) {
        var m = parts[i].trim().match(/\s(\d+)w$/);
        if (m) best = Math.max(best, parseInt(m[1], 10));
      }
      return best;
    }

    function resolveZoom() {
      var a = gal.querySelector('.swiper-slide-active a[data-fancybox]');
      var img = gal.querySelector('.swiper-slide-active img');
      var href = a ? absolute(depad(a.getAttribute('href'))) : '';

      zoomUrl = null;
      lensLoaded = false;
      lens.style.backgroundImage = '';

      /* Only the hover hint is tied to whether a zoom is available. On touch
         this same element is the button that opens the viewer, and that works
         for every product no matter how small its master is - hiding it below
         the zoom gate left low-resolution products with no affordance at all. */
      if (fine.matches) hint.style.display = 'none';

      if (!href || !fine.matches || calm.matches) return;

      var width = masterWidth(img);
      if (width < MIN_ZOOM_WIDTH) return;

      /* Magnify by however much headroom the master actually has over the
         size it is displayed at, clamped at both ends. A 1000px file in a
         680px tile zooms ~1.5x; a 1920px file zooms the full 2.4x. */
      var scale = Math.max(LENS_MIN_SCALE,
                  Math.min(LENS_MAX_SCALE, width / tileWidth()));

      zoomUrl = href;
      lensScale = Math.round(scale * 100) + '%';
      hint.style.display = '';
    }

    /* Everything here is deferred to the next frame. None of it is needed to
       start the transition, and two of the steps (reading the active
       thumbnail's offsetTop, then writing custom properties) force a
       synchronous layout. Running that inside the click meant the browser had
       to finish it before it could paint the first frame of the slide, which
       is what made switching feel like it caught. Queued once per frame, so a
       burst of Swiper class mutations cannot pile up. */
    var syncQueued = false;

    function sync() {
      var i = activeIndex();
      if (i === lastIndex) return;
      lastIndex = i;

      if (syncQueued) return;
      syncQueued = true;

      window.requestAnimationFrame(function () {
        syncQueued = false;
        depadAll(gal);
        moveMarker();
        resolveZoom();
        outer.classList.remove('rt-zooming');
      });
    }

    /* ---- zoom lens ---- */
    var pending = null;
    outer.addEventListener('pointerenter', function () {
      if (!zoomUrl) return;

      /* The big file is fetched here, on hover intent, rather than on every
         slide change. Someone paging through photos never pays for it. */
      if (!lensLoaded) {
        lens.style.backgroundImage = 'url("' + zoomUrl + '")';
        lens.style.backgroundSize = lensScale;
        lensLoaded = true;
      }
      rect = outer.getBoundingClientRect();
      outer.classList.add('rt-zooming');
    }, { passive: true });

    outer.addEventListener('pointerleave', function () {
      outer.classList.remove('rt-zooming');
    }, { passive: true });

    outer.addEventListener('pointermove', function (e) {
      if (!zoomUrl || !rect) return;
      var cx = e.clientX, cy = e.clientY;
      if (pending) return;
      pending = window.requestAnimationFrame(function () {
        pending = null;
        var x = ((cx - rect.left) / rect.width) * 100;
        var y = ((cy - rect.top) / rect.height) * 100;
        lens.style.backgroundPosition =
          Math.max(0, Math.min(100, x)).toFixed(2) + '% ' +
          Math.max(0, Math.min(100, y)).toFixed(2) + '%';
      });
    }, { passive: true });

    /* ---- preload on hover intent ---- */
    if (bullets) {
      /* Dwell before preloading. Without the delay, sweeping the pointer down
         the rail fired a full-size download for every thumbnail it crossed. */
      var dwell = null;
      bullets.addEventListener('pointerenter', function (e) {
        var b = e.target.closest ? e.target.closest('.swiper-pagination-bullet') : null;
        if (!b) return;
        window.clearTimeout(dwell);
        dwell = window.setTimeout(function () {
          var idx = Array.prototype.indexOf.call(
            bullets.querySelectorAll('.swiper-pagination-bullet'), b);
          var a = gal.querySelectorAll('a[data-fancybox]')[idx];
          if (!a) return;
          var href = absolute(depad(a.getAttribute('href')));
          if (href && !probed[href]) { probed[href] = 1; var im = new Image(); im.src = href; }
        }, 180);
      }, { capture: true, passive: true });

      bullets.addEventListener('pointerleave', function () {
        window.clearTimeout(dwell);
      }, { capture: true, passive: true });
    }

    /* ---- keyboard ---- */
    gal.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var btn = gal.querySelector(
        e.key === 'ArrowRight' ? '.swiper-button-next' : '.swiper-button-prev');
      if (btn && !btn.classList.contains('swiper-button-disabled')) {
        e.preventDefault();
        btn.click();
      }
    });

    /* ---- watch the active thumbnail ---- */
    if (bullets && window.MutationObserver) {
      new MutationObserver(sync).observe(bullets, {
        subtree: true, attributes: true, attributeFilter: ['class']
      });
    }
    if (window.MutationObserver) {
      new MutationObserver(function () { depadAll(gal); sync(); })
        .observe(gal, { childList: true, subtree: true });
    }

    window.addEventListener('resize', function () {
      markerH = 0;
      moveMarker();
      rect = null;
      tileW = 0;
    }, { passive: true });

    /* The height ceiling is derived from the tile's own width, so it has to be
       recomputed whenever that width actually changes. A debounced resize
       listener measured mid-layout and produced a tile sized from the previous
       viewport's width; a ResizeObserver fires after layout instead. Guarded on
       width so our own height change cannot re-trigger it. */
    if (window.ResizeObserver) {
      var lastWidth = 0;
      new ResizeObserver(function (entries) {
        /* fitTiles guards itself while the lightbox is open, so this keeps
           tracking widths normally and the work is dropped at the choke point. */
        var w = Math.round(entries[0].contentRect.width);
        if (!w || w === lastWidth) return;
        lastWidth = w;
        tileW = 0;
        appliedRatio = null;
        fitTiles();
        moveMarker();
      }).observe(outer);
    }

    relayout = function () {
      appliedRatio = null;
      fitTiles();
      moveMarker();
    };

    fitTiles();
    sync();
    moveMarker();
  }

  /* ------------------------------------------------- sticky header offset */

  function stickyOffset() {
    var host = document.getElementById('main-product');
    if (!host) return;
    var best = 0;
    var nodes = document.querySelectorAll('header, .sticky, [class*="sticky"]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var cs = window.getComputedStyle(el);
      if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
      var r = el.getBoundingClientRect();
      if (r.top > 4 || r.height < 30 || r.height > 200) continue;
      if (r.height > best) best = r.height;
    }
    if (best) host.style.setProperty('--rt-gal-top', Math.round(best + 16) + 'px');
  }

  /* --------------------------------------------- scope the lightbox to PDP */

  /* Set by setup(). The lightbox close handler uses it to recover any resize
     that happened while the viewer was covering the page. */
  var relayout = null;

  function scopeLightbox() {
    var root = document.documentElement;
    var open = false;      /* a lightbox holding OUR images is on screen */
    var pending = null;    /* optimistic class awaiting confirmation */

    /* Does this container hold one of our gallery's images? Fancybox is shared
       with the rest of the theme, so ownership is decided by comparing the
       viewer's image sources against our anchors' hrefs rather than by trusting
       whichever click happened last. */
    function ours(c) {
      var gal = document.querySelector(GAL_SEL);
      if (!gal) return false;

      var map = {}, anchors = gal.querySelectorAll('a[data-fancybox]'), i, h;
      for (i = 0; i < anchors.length; i++) {
        h = (anchors[i].getAttribute('href') || '').split('?')[0];
        if (h) map[h] = 1;
      }
      var imgs = c.querySelectorAll('img');
      for (i = 0; i < imgs.length; i++) {
        var src = (imgs[i].getAttribute('src') || '').split('?')[0];
        if (src && map[src]) return true;
      }
      return false;
    }

    /* Apply optimistically on click so the viewer is styled from its first
       frame, then back it out if no lightbox actually appears. */
    document.addEventListener('click', function (e) {
      var a = e.target.closest ? e.target.closest('a[data-fancybox]') : null;
      if (!a || !a.closest('#main-product .l4pr')) return;

      root.classList.add('rt-pdp-lightbox');
      if (pending) window.clearTimeout(pending);
      pending = window.setTimeout(function () {
        pending = null;
        if (!open) root.classList.remove('rt-pdp-lightbox');
      }, 2500);

      /* The theme loads Fancybox on first use: window.Fancybox is undefined
         until something opens a lightbox. On a cold page nothing has cancelled
         this anchor yet, so the browser follows its href and lands on the raw
         image file on the CDN - which is why the first click looked like it
         navigated away, and why a second click behaved normally. Hold the
         navigation and replay the click once the library has arrived. Not
         passive, because this needs preventDefault. */
      if (typeof window.Fancybox === 'undefined' &&
          !document.querySelector('.fancybox__container')) {
        e.preventDefault();

        var waited = 0;
        (function settle() {
          /* the theme got there first */
          if (document.querySelector('.fancybox__container')) return;
          /* library is in: replay the click, which this handler now lets pass */
          if (typeof window.Fancybox !== 'undefined') { a.click(); return; }

          waited += 100;
          if (waited < 6000) { window.setTimeout(settle, 100); return; }
          /* give up and do what the browser would have done anyway */
          window.location.assign(a.href);
        })();
      }
    }, { capture: true });

    if (!window.MutationObserver) return;

    new MutationObserver(function () {
      var c = document.querySelector('.fancybox__container');

      if (c) {
        if (open) return;
        /* Fancybox appends the container before filling in its slides, so
           confirm ownership over a few frames instead of once. */
        var tries = 0;
        (function confirm() {
          var cur = document.querySelector('.fancybox__container');
          if (!cur || open) return;
          if (ours(cur)) {
            open = true;
            if (pending) { window.clearTimeout(pending); pending = null; }
            root.classList.add('rt-pdp-lightbox');
            return;
          }
          if (++tries < 6) window.setTimeout(confirm, 80);
        })();
        return;
      }

      /* Only a container we confirmed counts as a close. Acting on the bare
         absence of one used to strip the class during the body mutations that
         precede the container being appended, so the styling never applied. */
      if (!open) return;

      open = false;
      if (pending) { window.clearTimeout(pending); pending = null; }
      root.classList.remove('rt-pdp-lightbox');
      /* Layout was frozen while the viewer covered the page, so catch up in
         case the window changed size behind it. */
      if (relayout) window.setTimeout(relayout, 60);
    }).observe(document.body, { childList: true });
  }

  /* ------------------------------------------------------------- bootstrap */

  function boot() {
    scopeLightbox();
    stickyOffset();

    var tries = 0;
    var done = false;
    (function wait() {
      if (done) return;
      var gal = document.querySelector(GAL_SEL);
      /* de-pad as soon as the markup exists, without waiting for Swiper */
      if (gal) depadAll(gal);
      if (gal && gal.classList.contains('l4pr-initialized')) { done = true; setup(gal); return; }
      if (++tries > 120) { done = true; if (gal) setup(gal); return; }
      /* Both timers, because requestAnimationFrame is throttled to a standstill
         in a background tab: a PDP opened in one would otherwise never set up
         until it was brought to the front. Whichever fires first wins, and
         `done` keeps setup single-shot. */
      window.requestAnimationFrame(wait);
      window.setTimeout(wait, 32);
    })();

    var t;
    window.addEventListener('resize', function () {
      window.clearTimeout(t);
      t = window.setTimeout(stickyOffset, 180);
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
